import { CalendarToday } from '@mui/icons-material';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  FormControl,
  InputAdornment,
  Popover,
  TextField,
  TextFieldProps,
  styled,
} from '@mui/material';
import { format } from 'date-fns';
import {
  DateRange,
  DateRangePicker as MuiDatePicker,
} from 'mui-daterange-picker';
import React, { useState } from 'react';

type DateRangePickerType = Omit<BoxProps, 'onChange'> & {
  dateFormat?: string;
  onChange?: (range: DateRange) => void;
  onClose?: () => void;
  startLabel?: string;
  startDate?: Date;
  size?: TextFieldProps['size'];
  endLabel?: string;
  endDate?: Date;
  inputProps?: TextFieldProps;
};

const DateRangePicker: React.FC<DateRangePickerType> = ({
  dateFormat = 'MM/dd/yyyy',
  onChange = () => undefined,
  onClose = () => undefined,
  startLabel = 'Start date',
  endLabel = 'End date',
  size = 'medium',
  inputProps,
  startDate,
  endDate,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({ startDate, endDate });
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChange = (range: DateRange) => {
    setDateRange(range);
    onChange(range);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose();
  };

  return (
    <Box {...props}>
      <PickerButton fullWidth onClick={handleClick} isActive={!!anchorEl}>
        <FormControl sx={{ minWidth: 120, flex: 1 }} size={size}>
          <TextField
            label={startLabel}
            size={size}
            value={
              dateRange.startDate ? format(dateRange.startDate, dateFormat) : ''
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarToday />
                </InputAdornment>
              ),
            }}
            {...inputProps}
          />
        </FormControl>
        <FormControl sx={{ minWidth: 120, flex: 1 }} size={size}>
          <TextField
            label={endLabel}
            size={size}
            value={
              dateRange.endDate ? format(dateRange.endDate, dateFormat) : ''
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarToday />
                </InputAdornment>
              ),
            }}
            {...inputProps}
          />
        </FormControl>
      </PickerButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        sx={{ overflow: 'hidden !important' }}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        <MuiDatePicker
          open={!!anchorEl}
          wrapperClassName="MuiDatePicker"
          toggle={handleClose}
          onChange={handleChange}
        />
      </Popover>
    </Box>
  );
};

interface PickerButtonProps extends ButtonProps {
  isActive: boolean;
}
const PickerButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<PickerButtonProps>(({ theme, isActive }) => ({
  display: 'flex',
  padding: 0,
  gap: theme.spacing(2),
  '& .MuiInputBase-root': {
    cursor: 'pointer',
  },
  '& .MuiInputAdornment-root svg': {
    fontSize: '1.25rem',
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.primary,
  },
  '&:hover': {
    background: 'transparent',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.primary.main} !important`,
    },
  },
  ...(isActive && {
    '& .MuiFormLabel-root': {
      color: `${theme.palette.primary.main} !important`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
      borderColor: `${theme.palette.primary.main} !important`,
    },
  }),
  // mui-datepicker overrides
  '& .MuiDatePicker': {},
}));

export default DateRangePicker;
